// Pure projection of a stage-rename map for the save flow.
//
// `baseline` is the {name → label} the briefing had BEFORE the
// producer's edits — sourced from the DB (briefings.stages JSONB
// snapshot taken at the very top of handleSave) rather than from
// React state. This matters because the original implementation
// read `originalStageLabels` from React state, which:
//
//   * starts empty until the load-effect populates it (race
//     window during initial render);
//   * gets overwritten when existingBriefing refetches mid-save
//     (saveProduction fires queryClient.invalidateQueries before
//     saveRelatedData runs);
//   * captures whatever value the closure had at the render the
//     handler was bound — drift between handler creation and
//     handler invocation can mismatch the user's expected baseline.
//
// Field symptom that surfaced this: producer renamed stages
// "Area 2" → "Area 2 - hard house"; briefings.stages JSONB and
// briefing_stages got the new labels; lineup + crew_assignments
// stayed on "Area 2" because the rename map was empty so
// resolveStage was a no-op. ReadOnlyScheduleTimeline matched
// nothing under the renamed stage — view-only schedule lost all
// artist blocks, only custom subjects remained.
//
// Snapshotting from DB before any save writes guarantees the
// baseline is the persisted truth, not whatever the React state
// happens to hold.

export type StageLike = { name: string; label: string };

export function buildStageRenameMap(input: {
  baseline: StageLike[];
  current: StageLike[];
}): Map<string, string> {
  const baselineByName = new Map(input.baseline.map((s) => [s.name, s.label]));
  const baselineLabels = new Set(input.baseline.map((s) => s.label).filter(Boolean));
  const currentLabels = new Set(input.current.map((s) => s.label).filter(Boolean));
  const map = new Map<string, string>();
  for (const s of input.current) {
    if (!s.label) continue;
    const old = baselineByName.get(s.name);
    // Only a GENUINE rename: the old label is gone from the current stage set
    // AND the new label wasn't already one of the stages. Without these guards
    // a pure REORDER misfires as a cyclic rename — stage `name` is positional
    // (`day{d}_stage{n}`), so reordering keeps the names in place while the
    // labels permute; matching baseline↔current by name then reads every moved
    // stage as "renamed to the label now sitting in its slot", shifting every
    // stage's label on the lineup + crew_assignments on the next save.
    if (old && old !== s.label && !currentLabels.has(old) && !baselineLabels.has(s.label)) {
      map.set(old, s.label);
    }
  }
  return map;
}
